import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import { AppComponent } from './app.component';
// =============================================================================
// 3rd Party
// =============================================================================
import { RecaptchaModule } from 'ng-recaptcha';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

// =============================================================================
// Routes
// =============================================================================
import { appRoutes } from './app.routes';

// =============================================================================
// Directives
// =============================================================================
import { StsCmsImageDirective } from './directives/sts-cms-image/sts-cms-image.directive';
import { StsCmsTxtDirective } from './directives/sts-cms-txt/sts-cms-txt.directive';
import { StsRegexInputDirective } from './directives/sts-regex-input/sts-regex-input.directive';
import { StsUniqueEmailValidatorDirective } from './directives/validators/sts-unique-email-validator.directive';
import { StsConditionalEmailValidatorDirective } from './directives/validators/sts-conditional-email-validator.directive';

// =============================================================================
// Services
// =============================================================================
import { StsCmsService } from './services/sts-cms-service.service';
import { StsHttpService } from './services/sts-http.service';
import { StsConfigService } from './services/sts-config.service';
import { StsPortalStatusService } from './services/sts-portal-status.service';
import { StsRouteRequiresUnauth } from './services/sts-route-requires-unauth';
import { StsRouteRequiresAuth } from './services/sts-route-requires-auth';
import { StsTenantService } from './services/sts-tenant.service';
import { StsRecaptchaService} from './services/sts-recaptcha.service';
import { StsDialogFactoryService } from './services/sts-dialog-factory.service';
import { StsSessionTimeoutService } from './services/sts-session-timeout.service';
import {StsA360RegServicesService} from './services/sts-a360-reg-services.service';
import {StsUserService} from './services/sts-user.service';
import {StsTfaRegServiceService} from './services/sts-tfa-reg-service.service';

// =============================================================================
// Pipes
// =============================================================================
import { UniqueQuestionsPipe } from './pipes/unique-questions/unique-questions.pipe';
import { StsSafeHtmlPipe } from './pipes/sts-safe-html/sts-safe-html.pipe';
import { StsSafeUrlPipe } from './pipes/sts-safe-url/sts-safe-url.pipe';


// =============================================================================
// Components
// =============================================================================
import { AccountSigninComponent } from './components/account/account-signin/account-signin.component';
import { AccountForgotPasswordComponent } from './components/account/account-forgot-password/account-forgot-password.component';
import { AccountContactUsComponent } from './components/account/account-contact-us/account-contact-us.component';
import { AccountPreselectedQuestionsComponent }
from './components/account/account-preselected-questions/account-preselected-questions.component';


import { HeaderComponent } from './components/common/sts-header/header.component';
import { FooterComponent } from './components/common/sts-footer/footer.component';

import { StsSectionHeaderComponent } from './components/common/sts-section-header/sts-section-header/sts-section-header.component';
import { StsSectionBodyComponent } from './components/common/sts-section-body/sts-section-body.component';
import { StsSectionFooterComponent } from './components/common/sts-section-footer/sts-section-footer.component';
import { StsBsDialogComponent } from './components/common/sts-bs-dialog/sts-bs-dialog/sts-bs-dialog.component';
import { AccountHelpComponent } from './components/account/account-help/account-help.component';
import { StsLowerBarComponent } from './components/common/sts-lower-bar/sts-lower-bar/sts-lower-bar.component';
import { AccountPasswordComponent } from './components/account/account-password/account-password.component';
import { AccountAnswerQuestionsComponent } from './components/account/account-answer-questions/account-answer-questions.component';
import { AccountDisclaimerComponent } from './components/account/account-disclaimer/account-disclaimer.component';
import { StsFieldNotificationDirective } from './directives/sts-field-notification/sts-field-notification.directive';
import { StsShowErrorComponent } from './components/common/sts-show-error/sts-show-error/sts-show-error.component';
import { AccountVoidedComponent } from './components/account/account-voided/account-voided.component';
import { StsButtonComponent } from './components/common/sts-button/sts-button.component';
import { StsCaptchaComponent } from './components/common/sts-captcha/sts-captcha.component';
import { AccountThankYouComponent } from './components/account/account-thank-you/account-thank-you.component';
import { AccountRegPickUserNameComponent } from './components/account/account-reg-pick-user-name/account-reg-pick-user-name.component';
import { AccountForgotUsernameComponent } from './components/account/account-forgot-username/account-forgot-username.component';
import { StsSelfIdentifiersComponent } from './components/common/sts-self-identifiers/sts-self-identifiers.component';
import { StsHeaderBannerComponent } from './components/common/sts-header-banner/sts-header-banner.component';
import { AccountRegValidateEmailComponent } from './components/account/account-reg-validate-email/account-reg-validate-email.component';
import { AccountRegConfirmEmailComponent } from './components/account/account-reg-confirm-email/account-reg-confirm-email.component';
import { AccountRegCollectEmailComponent } from './components/account/account-reg-collect-email/account-reg-collect-email.component';
import { AccountTwoFactorVerifyComponent } from './components/account/account-two-factor-verify/account-two-factor-verify.component';
import { AccountTwoFactorSetupComponent } from './components/account/account-two-factor-setup/account-two-factor-setup.component';
import { AccountSelCommChannelComponent } from './components/account/account-sel-comm-channel/account-sel-comm-channel.component';
import { AccountRegVerifyOtcComponent } from './components/account/account-reg-verify-otc/account-reg-verify-otc.component';
import { AccountRegSelCommChannelComponent } from './components/account/account-reg-sel-comm-channel/account-reg-sel-comm-channel.component';

import { AccountForgotPasswordEmailComponent }
from './components/account/account-forgot-password-email/account-forgot-password-email.component';

import { StsSectionHeaderSideComponent }
from './components/common/sts-section-header/sts-section-header-side/sts-section-header-side.component';

import { AccountRegIdentityQuestionsComponent }
from './components/account/account-reg-identity-questions/account-reg-identity-questions.component';

import { AccountRegWrongPostalCodeComponent }
from './components/account/account-reg-wrong-postal-code/account-reg-wrong-postal-code.component';

import { AccountVerifyEmployerCodeComponent }
from './components/account/account-verify-employer-code/account-verify-employer-code.component';

import { StsRecaptchaComponent }
from './components/common/sts-captcha/sts-recaptcha/sts-recaptcha.component';

import { StsTextCaptchaComponent }
from './components/common/sts-captcha/sts-text-captcha/sts-text-captcha.component';

export function getWindow() { return window; }

// =============================================================================
@NgModule({
  declarations: [
    AppComponent,
    AccountContactUsComponent,
    HeaderComponent,
    FooterComponent,
    StsCmsImageDirective,
    StsCmsTxtDirective,
    StsBsDialogComponent,
    StsSectionHeaderComponent,
    StsSectionBodyComponent,
    StsSectionFooterComponent,
    AccountPreselectedQuestionsComponent,
    AccountSigninComponent,
    AccountForgotPasswordComponent,
    StsSafeUrlPipe,
    AccountHelpComponent,
    StsLowerBarComponent,
    AccountPasswordComponent,
    AccountAnswerQuestionsComponent,
    AccountDisclaimerComponent,
    StsFieldNotificationDirective,
    StsShowErrorComponent,
    UniqueQuestionsPipe,
    AccountForgotPasswordEmailComponent,
    AccountVoidedComponent,
    StsSafeHtmlPipe,
    StsButtonComponent,
    StsCaptchaComponent,
    AccountThankYouComponent,
    StsSectionHeaderSideComponent,
    AccountRegIdentityQuestionsComponent,
    AccountRegWrongPostalCodeComponent,
    AccountRegPickUserNameComponent,
    StsRegexInputDirective,
    StsUniqueEmailValidatorDirective,
    StsConditionalEmailValidatorDirective,
    AccountForgotUsernameComponent,
    StsSelfIdentifiersComponent,
    AccountVerifyEmployerCodeComponent,
    StsHeaderBannerComponent,
    AccountRegValidateEmailComponent,
    AccountRegConfirmEmailComponent,
    AccountRegCollectEmailComponent,
    AccountTwoFactorVerifyComponent,
    AccountSelCommChannelComponent,
    AccountRegVerifyOtcComponent,
    AccountRegSelCommChannelComponent,
    AccountTwoFactorSetupComponent,
    StsRecaptchaComponent,
    StsTextCaptchaComponent
  ],

  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    HttpClientModule,
    appRoutes,
    ReactiveFormsModule,
    RecaptchaModule.forRoot(),
    BsDropdownModule.forRoot()
  ],
  providers: [

    StsTenantService,
    StsCmsService,
    StsHttpService,
    StsConfigService,
    StsPortalStatusService,
    StsDialogFactoryService,
    StsSessionTimeoutService,
    StsRouteRequiresUnauth,
    StsRouteRequiresAuth,
    StsA360RegServicesService,
    StsUserService,
    StsTfaRegServiceService,
    StsRecaptchaService
  ],
  entryComponents: [StsBsDialogComponent],
  bootstrap: [AppComponent]
})


export class AppModule {
}
